import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface AddressBook {
  id: string;
  name: string;
  description: string | null;
  parentId: string | null;
  parent: { id: string; name: string } | null;
  createdAt: string;
  _count: { devices: number };
}

interface PaginatedAddressBooks {
  data: AddressBook[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AddressBookFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

export function useAddressBooks(filters: AddressBookFilters = {}) {
  return useQuery({
    queryKey: ['address-books', filters],
    queryFn: () =>
      api.get<PaginatedAddressBooks>('/address-books', {
        query: {
          page: filters.page ?? 1,
          pageSize: filters.pageSize ?? 50,
          search: filters.search,
        },
      }),
    placeholderData: (prev) => prev,
  });
}

/** Light helper for selects that just need the full list (ids + names). */
export function useAddressBooksAll() {
  return useQuery({
    queryKey: ['address-books', 'all'],
    queryFn: () => api.get<PaginatedAddressBooks>('/address-books', { query: { pageSize: 200 } }),
    staleTime: 60_000,
  });
}

export interface CreateAddressBookInput {
  name: string;
  description?: string;
  parentId?: string | null;
}

export function useCreateAddressBook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAddressBookInput) => api.post<AddressBook>('/address-books', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['address-books'] }),
  });
}

export function useUpdateAddressBook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CreateAddressBookInput> }) =>
      api.patch<AddressBook>(`/address-books/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['address-books'] }),
  });
}

export function useDeleteAddressBook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/address-books/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['address-books'] }),
  });
}
